<template>
	<div class="animated fadeIn">

		<b-row class="mb-3 align-items-center">
			<b-col>
				<h4 class="mb-0"><i class="fa fa-server mr-2 text-primary"></i>Systems</h4>
				<small class="text-muted">The registry every monitoring page reads from</small>
			</b-col>
			<b-col class="text-right">
				<b-button v-if="editable && $store.getters.canWrite" variant="primary" size="sm" class="mr-1"
									@click="openCreate">
					<i class="fa fa-plus mr-1"></i>Add system
				</b-button>
				<b-button variant="outline-secondary" size="sm" @click="load" :disabled="loading">
					<i class="icon-reload mr-1"></i>Refresh
				</b-button>
			</b-col>
		</b-row>

		<div v-if="loading" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<div v-else>
			<b-alert v-if="loadError" show variant="warning" class="small">{{ loadError }}</b-alert>

			<template v-else>
				<!-- Where this list comes from -->
				<b-alert v-if="!editable" show variant="light" class="border small text-muted">
					<i class="fa fa-info-circle mr-1 text-primary"></i>
					<strong>Read-only.</strong> These systems are coming from
					<code>application.properties</code>, not the database, so they cannot be edited here.
					Create the <code>tipsoi_admin</code> schema, run
					<code>db/migration/tipsoi_admin_monitoring.sql</code> and set
					<code>admin.datasource.*</code> to switch this page to the database.
				</b-alert>

				<b-row class="mb-2 align-items-center">
					<b-col md="6" class="mb-2 mb-md-0">
						<b-input-group size="sm">
							<b-input-group-prepend is-text><i class="fa fa-search"></i></b-input-group-prepend>
							<b-form-input v-model="search" type="search" placeholder="Search system name or code…"/>
						</b-input-group>
					</b-col>
					<b-col md="6" class="text-md-right">
						<b-form-radio-group v-model="categoryFilter" size="sm" buttons button-variant="outline-secondary">
							<b-form-radio value="ALL">All</b-form-radio>
							<b-form-radio value="HRM">HRM</b-form-radio>
							<b-form-radio value="CS">CS</b-form-radio>
							<b-form-radio value="UNCHECKABLE">No health URL</b-form-radio>
						</b-form-radio-group>
					</b-col>
				</b-row>

				<b-card no-body>
					<div class="table-responsive">
						<table class="table table-sm table-striped table-hover mb-0">
							<thead>
								<tr>
									<th>System</th>
									<th>Code</th>
									<th>Category</th>
									<th>Health URL</th>
									<th class="text-right">Timeout</th>
									<th class="text-center">Enabled</th>
									<th v-if="editable && $store.getters.canWrite" class="text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="s in filtered" :key="s.code">
									<td>
										{{ s.name }}
										<b-badge v-if="s.local" variant="light" class="ml-1">local</b-badge>
									</td>
									<td><code>{{ s.code }}</code></td>
									<td><b-badge :variant="categoryVariant(s.category)">{{ s.category }}</b-badge></td>
									<td class="small">
										<span v-if="s.healthUrl" class="url">{{ s.healthUrl }}</span>
										<b-badge v-else variant="warning">not set</b-badge>
									</td>
									<td class="text-right num">{{ s.timeoutMs ? s.timeoutMs + ' ms' : '—' }}</td>
									<td class="text-center">
										<i v-if="s.enabled" class="fa fa-check text-success"></i>
										<span v-else class="text-muted">off</span>
									</td>
									<td v-if="editable && $store.getters.canWrite" class="text-right">
										<b-button size="sm" variant="warning" class="mr-1" @click="openEdit(s)">
											<i class="icon-pencil"></i>
										</b-button>
										<b-button size="sm" variant="danger" @click="confirmDelete(s)">
											<i class="fa fa-trash-o"></i>
										</b-button>
									</td>
								</tr>
								<tr v-if="!filtered.length">
									<td :colspan="editable && $store.getters.canWrite ? 7 : 6"
											class="text-center text-muted py-4">No system matches this filter.</td>
								</tr>
							</tbody>
						</table>
					</div>
				</b-card>

				<p class="text-muted tiny mt-3 mb-0">
					A system with no health URL is never checked — it appears on the Live board as
					<em>not configured</em> rather than being reported down, because nothing has been observed
					about it either way.
				</p>
			</template>
		</div>

		<!-- Add / edit -->
		<b-modal id="system-modal"
						 :title="form.id ? 'Edit ' + form.name : 'Add system'"
						 header-bg-variant="dark"
						 header-text-variant="light"
						 :ok-title="saving ? 'Saving…' : 'Save'"
						 :ok-disabled="saving"
						 @ok="handleSave"
						 @hidden="resetForm">
			<b-form>
				<b-form-group label="Code" :label-cols="4" :horizontal="true"
											description="Stable key used by health history. Changing it orphans past checks.">
					<b-form-input v-model="form.code" placeholder="LIVE_HRM" :disabled="!!form.id && lockCode"/>
				</b-form-group>
				<b-form-group label="Name" :label-cols="4" :horizontal="true">
					<b-form-input v-model="form.name" placeholder="Live HRM"/>
				</b-form-group>
				<b-form-group label="Category" :label-cols="4" :horizontal="true">
					<b-form-select v-model="form.category" :options="['HRM','CS','ADMIN','OTHER']"/>
				</b-form-group>
				<b-form-group label="Health URL" :label-cols="4" :horizontal="true"
											description="Unauthenticated health endpoint. Leave blank if there isn't one yet.">
					<b-form-input v-model="form.healthUrl" placeholder="https://example.com/health"/>
				</b-form-group>
				<b-form-group label="Method" :label-cols="4" :horizontal="true">
					<b-form-select v-model="form.healthMethod" :options="['GET','HEAD']"/>
				</b-form-group>
				<b-form-group label="Expected status" :label-cols="4" :horizontal="true">
					<b-form-input v-model.number="form.expectedStatus" type="number" min="100" max="599"/>
				</b-form-group>
				<b-form-group label="Timeout (ms)" :label-cols="4" :horizontal="true">
					<b-form-input v-model.number="form.timeoutMs" type="number" min="500" max="60000"/>
				</b-form-group>
				<b-form-group label="Degraded above (ms)" :label-cols="4" :horizontal="true"
											description="Responses slower than this report DEGRADED instead of UP. Blank uses the default.">
					<b-form-input v-model.number="form.degradedAboveMs" type="number" min="1"/>
				</b-form-group>
				<b-form-group label="API base URL" :label-cols="4" :horizontal="true">
					<b-form-input v-model="form.apiBaseUrl" placeholder="optional"/>
				</b-form-group>
				<b-form-group label="Sort order" :label-cols="4" :horizontal="true">
					<b-form-input v-model.number="form.sortOrder" type="number"/>
				</b-form-group>
				<b-form-group :label-cols="4" :horizontal="true" label="Flags">
					<b-form-checkbox v-model="form.enabled" class="d-inline-block mr-3">Enabled</b-form-checkbox>
					<b-form-checkbox v-model="form.local" class="d-inline-block">Local</b-form-checkbox>
				</b-form-group>
				<b-alert v-if="formError" show variant="danger" class="small mb-0">{{ formError }}</b-alert>
			</b-form>
		</b-modal>

		<b-modal id="system-delete-modal"
						 title="Remove system"
						 header-bg-variant="dark"
						 header-text-variant="light"
						 ok-title="Remove"
						 ok-variant="danger"
						 @ok="doDelete">
			Remove <strong>{{ toDelete.name }}</strong> from the registry? It disappears from the Live
			board, but its recorded health history is kept.
		</b-modal>
	</div>
</template>

<script>
import monitoringService from '@/services/monitoringService';
import toastService from '@/services/toastService';

function blankForm() {
	return {
		id: null,
		code: '',
		name: '',
		category: 'OTHER',
		healthUrl: '',
		healthMethod: 'GET',
		expectedStatus: 200,
		timeoutMs: 10000,
		degradedAboveMs: null,
		apiBaseUrl: '',
		sortOrder: 100,
		enabled: true,
		local: false
	};
}

export default {
	name: 'MonitoringSystems',

	data() {
		return {
			loading: false,
			saving: false,
			loadError: '',
			formError: '',
			search: '',
			categoryFilter: 'ALL',
			systems: [],
			editable: false,
			form: blankForm(),
			lockCode: true,
			toDelete: {}
		};
	},

	computed: {
		filtered() {
			const term = this.search.trim().toLowerCase();
			return this.systems.filter(s => {
				if (this.categoryFilter === 'UNCHECKABLE') {
					if (s.healthUrl) return false;
				} else if (this.categoryFilter !== 'ALL' && s.category !== this.categoryFilter) {
					return false;
				}
				if (!term) return true;
				return (s.name || '').toLowerCase().indexOf(term) !== -1
					|| (s.code || '').toLowerCase().indexOf(term) !== -1;
			});
		}
	},

	methods: {
		load() {
			this.loading = true;
			this.loadError = '';

			monitoringService.getSystems((data) => {
				this.loading = false;
				this.systems = (data && data.systems) || [];
				this.editable = !!(data && data.editable);
			}, (err) => {
				this.loading = false;
				this.loadError = err;
			});
		},

		openCreate() {
			this.form = blankForm();
			this.lockCode = false;
			this.formError = '';
			this.$bvModal.show('system-modal');
		},

		openEdit(system) {
			this.form = Object.assign(blankForm(), system);
			// Editing the code silently detaches the system from its own history, so it stays locked
			// unless somebody deliberately unlocks it.
			this.lockCode = true;
			this.formError = '';
			this.$bvModal.show('system-modal');
		},

		handleSave(event) {
			event.preventDefault();
			this.formError = '';

			if (!this.form.code || !this.form.name) {
				this.formError = 'Code and name are both required.';
				return;
			}

			this.saving = true;
			const done = () => { this.saving = false; };

			const onOk = () => {
				done();
				this.$bvModal.hide('system-modal');
				toastService.showSuccessToast('System saved');
				this.load();
			};
			const onErr = (err) => {
				done();
				this.formError = err;
			};

			if (this.form.id) {
				monitoringService.updateSystem(this.form.id, this.form, onOk, onErr);
			} else {
				monitoringService.createSystem(this.form, onOk, onErr);
			}
		},

		confirmDelete(system) {
			this.toDelete = system;
			this.$bvModal.show('system-delete-modal');
		},

		doDelete() {
			monitoringService.deleteSystem(this.toDelete.id, () => {
				toastService.showSuccessToast('System removed');
				this.load();
			}, (err) => {
				toastService.showErrorToast(err);
			});
		},

		resetForm() {
			this.form = blankForm();
			this.formError = '';
		},

		categoryVariant(category) {
			if (category === 'HRM') return 'primary';
			if (category === 'CS') return 'info';
			if (category === 'ADMIN') return 'secondary';
			return 'light';
		}
	},

	created() {
		this.load();
	}
};
</script>

<style scoped>
.num { font-variant-numeric: tabular-nums; }
.tiny { font-size: 0.78rem; }

.url {
	word-break: break-all;
	font-family: monospace;
	font-size: 0.78rem;
}
</style>
